import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { ReportsController } from "../../../api/src/reports/reports.controller";
import type {
  Report,
  ReportBatch,
  ReportBatchForm,
  ReportBatchWithReports,
  ReportTemplate,
  ReportTemplateForm,
} from "../../../api/src/reports/reports.service";
import { useMemo } from "react";
import {
  deserializeDate,
  useAuthenticatedMutationNew,
  useAuthenticatedQuery,
} from "../lib/api";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";

/**
 * The published path of `getAllReportBatches`. The four report batch handlers
 * serve at `/v1` only (APP-1649); the keys below carry the same `v1` segment.
 */
export const REPORT_BATCHES_PATH = "/v1/reports/batch";

/**
 * Every report batch of the firm. `GET /v1/reports/batch` answers a page
 * (APP-1649) and `ReportBatches.tsx` shows every batch, so this walks every
 * page and holds `data` back until the last one is in.
 */
export function useQueryReportBatches() {
  const { rows, hasEveryRow, isError, isFetching, retry } =
    useAuthenticatedFetchAllPages<
      SerializedObject<
        UnpackResponse<ReportsController["getAllReportBatches"]>
      >["data"][number]
    >(REPORT_BATCHES_PATH);

  const data: ReportBatch[] | undefined = useMemo(
    () => (hasEveryRow ? rows.map(deserializeReportBatch) : undefined),
    [rows, hasEveryRow],
  );

  return {
    // A retry after a failed read shows the loading state again.
    isPending: !hasEveryRow && (!isError || isFetching),
    isError: isError && !isFetching,
    data,
    retry,
  };
}

export function useQueryReportBatch(batchId: number) {
  return useAuthenticatedQuery<
    UnpackResponse<ReportsController["getReportBatch"]>,
    ReportBatchWithReports | undefined,
    [string, string, string, number]
  >({
    queryKey: ["v1", "reports", "batch", batchId],
    queryFn: async (batch) =>
      typeof batch === "undefined"
        ? undefined
        : {
            ...deserializeReportBatch(batch.data),
            reports: batch.data.reports.map(deserializeReport),
          },
  });
}

export function useCreateReportBatch() {
  return useAuthenticatedMutationNew<
    UnpackResponse<ReportsController["createReportBatch"]>,
    ReportBatchWithReports,
    ReportBatchForm
  >({
    mutationKey: ["POST", "v1", "reports", "batch"],
    // APP-1649: the POST answers the batch it created, not its id.
    mutationFn: ({ data }) => ({
      ...deserializeReportBatch(data),
      reports: data.reports.map(deserializeReport),
    }),
  });
}

export function useDeleteReportBatch(reportBatchId: number) {
  return useAuthenticatedMutationNew<
    UnpackResponse<ReportsController["deleteReportBatch"]>,
    void,
    void
  >({
    mutationKey: ["DELETE", "v1", "reports", "batch", reportBatchId],
    mutationFn: () => null,
  });
}

/**
 * The published path of `getAllReportTemplates`. The five report template
 * handlers serve at `/v1` only (APP-1649); the keys below carry the same `v1`
 * segment.
 */
export const REPORT_TEMPLATES_PATH = "/v1/reports/templates";

/**
 * Every report template of the firm. `GET /v1/reports/templates` answers a
 * page (APP-1649), and `ReportTemplates.tsx` and the template select of
 * `CreateReport.tsx` show every template, so this walks every page and holds
 * `data` back until the last one is in.
 */
export function useQueryReportTemplates() {
  const { rows, hasEveryRow, isError, isFetching, retry } =
    useAuthenticatedFetchAllPages<
      SerializedObject<
        UnpackResponse<ReportsController["getAllReportTemplates"]>
      >["data"][number]
    >(REPORT_TEMPLATES_PATH);

  const data: ReportTemplate[] | undefined = useMemo(
    () => (hasEveryRow ? rows.map(deserializeReportTemplate) : undefined),
    [rows, hasEveryRow],
  );

  // A retry after a failed read shows the loading state again.
  const isPending = !hasEveryRow && (!isError || isFetching);

  // `CreateReport.tsx` reads `isLoading`, `ReportTemplates.tsx` `isPending`.
  return {
    isPending,
    isLoading: isPending,
    isError: isError && !isFetching,
    data,
    retry,
  };
}

export function useQueryReportTemplate(
  templateId: number,
  { enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<ReportsController["getReportTemplate"]>,
    ReportTemplate | undefined,
    [string, string, string, number]
  >({
    queryKey: ["v1", "reports", "templates", templateId],
    queryFn: async (template) =>
      typeof template === "undefined"
        ? undefined
        : deserializeReportTemplate(template.data),
    queryOptions: { enabled },
  });
}

export function useCreateReportTemplate() {
  return useAuthenticatedMutationNew<
    UnpackResponse<ReportsController["createReportTemplate"]>,
    ReportTemplate,
    ReportTemplateForm
  >({
    mutationKey: ["POST", "v1", "reports", "templates"],
    // APP-1649: the POST answers the template it created, not its id.
    mutationFn: ({ data }) => deserializeReportTemplate(data),
  });
}

export function useUpdateReportTemplate(reportTemplateId: number) {
  return useAuthenticatedMutationNew<
    UnpackResponse<ReportsController["updateReportTemplate"]>,
    ReportTemplate,
    ReportTemplateForm
  >({
    mutationKey: ["PUT", "v1", "reports", "templates", reportTemplateId],
    // APP-1649: the PUT answers the template as updated.
    mutationFn: ({ data }) => deserializeReportTemplate(data),
  });
}

export function useDeleteReportTemplate(reportTemplateId: number) {
  return useAuthenticatedMutationNew<
    UnpackResponse<ReportsController["deleteReportTemplate"]>,
    void,
    void
  >({
    mutationKey: ["DELETE", "v1", "reports", "templates", reportTemplateId],
    mutationFn: () => null,
  });
}

export function deserializeReportBatch(
  reportBatch: SerializedObject<ReportBatch>,
): ReportBatch {
  return {
    ...reportBatch,
    startDate: deserializeDate(reportBatch.startDate),
    endDate: deserializeDate(reportBatch.endDate),
    reportDate: deserializeDate(reportBatch.reportDate),
    createdTime: deserializeDate(reportBatch.createdTime),
    updatedTime: deserializeDate(reportBatch.updatedTime),
  };
}

export function deserializeReport(report: SerializedObject<Report>): Report {
  return {
    ...report,
    startDate: deserializeDate(report.startDate),
    endDate: deserializeDate(report.endDate),
    reportDate: deserializeDate(report.reportDate),
    createdTime: deserializeDate(report.createdTime),
    updatedTime: deserializeDate(report.updatedTime),
  };
}

export function deserializeReportTemplate(
  reportTemplate: SerializedObject<ReportTemplate>,
): ReportTemplate {
  return {
    ...reportTemplate,
    createdTime: deserializeDate(reportTemplate.createdTime),
    updatedTime: deserializeDate(reportTemplate.updatedTime),
  };
}
